import { authRequest } from './client';
import {
  type AcceptAmlCaseResponse,
  type AmlCaseDetailResponse,
  type AmlCaseListItem,
  type ChannelPaymentDetailResponse,
} from './types';

// ─── backoffice-api AML client ─────────────────────────────────────────────
//
// Thin fetch wrappers around backoffice-api's `/v1/aml` endpoints — the
// high-risk queue and its three resolution actions. All require
// AML_OFFICER (or SUPERADMIN), enforced by the backend. No pagination/
// filters on the list endpoint (backend doesn't support any). Reject/Refund
// return the same case-detail shape as `getAmlCase`; Accept deliberately
// does not (see `AcceptAmlCaseResponse`'s doc comment in types.ts).

export function listAmlCases(): Promise<AmlCaseListItem[]> {
  return authRequest<AmlCaseListItem[]>('/aml/cases');
}

export function getAmlCase(invoiceId: string): Promise<AmlCaseDetailResponse> {
  return authRequest<AmlCaseDetailResponse>(`/aml/cases/${invoiceId}`);
}

export function rejectAmlCase(invoiceId: string, note?: string): Promise<AmlCaseDetailResponse> {
  return authRequest<AmlCaseDetailResponse>(`/aml/cases/${invoiceId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export function refundAmlCase(invoiceId: string, note?: string): Promise<AmlCaseDetailResponse> {
  return authRequest<AmlCaseDetailResponse>(`/aml/cases/${invoiceId}/refund`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export function acceptAmlCase(invoiceId: string, note?: string): Promise<AcceptAmlCaseResponse> {
  return authRequest<AcceptAmlCaseResponse>(`/aml/cases/${invoiceId}/accept`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

// ─── Task 14 (feature-031 Phase B): channel-payment AML resolution ────────
//
// Same three actions, same decision-only shape (no address field — the
// merchant supplies the payout address later, from their own portal, same
// as the invoice refund flow above) — but addressing a held `ChannelPayment`
// (ACTION_REQUIRED/HIGH_RISK) instead of an invoice, and CHANNELS_MANAGER-
// gated rather than AML_OFFICER (per aml.controller.ts's method-level
// `@Roles` override on these three routes). All three return the same
// `ChannelPaymentDetailResponse` shape (already carries the `aml` audit
// block) — no separate response type needed, and no accept-vs-reject/refund
// shape split like the invoice queue's `AcceptAmlCaseResponse` (a channel-
// payment Accept is entirely local to backoffice-api, no RMQ round trip).

export function acceptChannelAmlCase(channelPaymentId: string, note?: string): Promise<ChannelPaymentDetailResponse> {
  return authRequest<ChannelPaymentDetailResponse>(`/aml/channel-cases/${channelPaymentId}/accept`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export function rejectChannelAmlCase(channelPaymentId: string, note?: string): Promise<ChannelPaymentDetailResponse> {
  return authRequest<ChannelPaymentDetailResponse>(`/aml/channel-cases/${channelPaymentId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export function refundChannelAmlCase(channelPaymentId: string, note?: string): Promise<ChannelPaymentDetailResponse> {
  return authRequest<ChannelPaymentDetailResponse>(`/aml/channel-cases/${channelPaymentId}/refund`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}
